import { useState, useEffect } from 'react';
import Constants from '../assests/Constants/constants';
import EnumsService from '../Services/enums.service';

/** Hardcoded fallbacks when case settings are unavailable (matches backend defaults). */
export const CASE_SYSTEM_ACCOUNT_DEFAULTS = {
    generalCheckCardId: Constants.PaymentAcceptanceConstantsAccountcards.GeneralCheck,
    acceptanceCheckCardId: Constants.PaymentAcceptanceConstantsAccountcards.GeneralCheck,
    paymentCheckCardId: Constants.PaymentAcceptanceConstantsAccountcards.GeneralCheck,
    generalCashCardId: Constants.PaymentAcceptanceConstantsAccountcards.GeneralCash,
    acceptanceCashCardId: Constants.PaymentAcceptanceConstantsAccountcards.GeneralCash,
    paymentCashCardId: Constants.PaymentAcceptanceConstantsAccountcards.GeneralCash,
    generalCreditCardCardId: Constants.PaymentAcceptanceConstantsAccountcards.GeneralCreditCard,
    acceptanceCreditCardCardId: Constants.PaymentAcceptanceConstantsAccountcards.GeneralCreditCard,
    paymentCreditCardCardId: Constants.PaymentAcceptanceConstantsAccountcards.GeneralCreditCard,
    generalBankCardId: Constants.PaymentAcceptanceConstantsAccountcards.GeneralBank,
    acceptanceBankTransactionCardId: Constants.PaymentAcceptanceConstantsAccountcards.GeneralBank,
    paymentBankTransactionCardId: Constants.PaymentAcceptanceConstantsAccountcards.GeneralBank,
    generalOffsetCardId: Constants.PaymentAcceptanceConstantsAccountcards.GeneralOffset,
    fixedAssetSaleSortCode: Constants.SaleOfEquipment,
    sortCodeDebit: '1',
    sortCodeCredit: '2',
    sortCodeDebitCredit: '3',
};

const mergeResolved = (resolved = {}) => ({
    ...CASE_SYSTEM_ACCOUNT_DEFAULTS,
    generalCheckCardId: resolved.generalCheckCardId ?? CASE_SYSTEM_ACCOUNT_DEFAULTS.generalCheckCardId,
    acceptanceCheckCardId: resolved.acceptanceCheckCardId ?? CASE_SYSTEM_ACCOUNT_DEFAULTS.acceptanceCheckCardId,
    paymentCheckCardId: resolved.paymentCheckCardId ?? CASE_SYSTEM_ACCOUNT_DEFAULTS.paymentCheckCardId,
    generalCashCardId: resolved.generalCashCardId ?? CASE_SYSTEM_ACCOUNT_DEFAULTS.generalCashCardId,
    acceptanceCashCardId: resolved.acceptanceCashCardId ?? CASE_SYSTEM_ACCOUNT_DEFAULTS.acceptanceCashCardId,
    paymentCashCardId: resolved.paymentCashCardId ?? CASE_SYSTEM_ACCOUNT_DEFAULTS.paymentCashCardId,
    generalCreditCardCardId: resolved.generalCreditCardCardId ?? CASE_SYSTEM_ACCOUNT_DEFAULTS.generalCreditCardCardId,
    acceptanceCreditCardCardId: resolved.acceptanceCreditCardCardId ?? CASE_SYSTEM_ACCOUNT_DEFAULTS.acceptanceCreditCardCardId,
    paymentCreditCardCardId: resolved.paymentCreditCardCardId ?? CASE_SYSTEM_ACCOUNT_DEFAULTS.paymentCreditCardCardId,
    generalBankCardId: resolved.generalBankCardId ?? CASE_SYSTEM_ACCOUNT_DEFAULTS.generalBankCardId,
    acceptanceBankTransactionCardId: resolved.acceptanceBankTransactionCardId ?? CASE_SYSTEM_ACCOUNT_DEFAULTS.acceptanceBankTransactionCardId,
    paymentBankTransactionCardId: resolved.paymentBankTransactionCardId ?? CASE_SYSTEM_ACCOUNT_DEFAULTS.paymentBankTransactionCardId,
    generalOffsetCardId: resolved.generalOffsetCardId ?? CASE_SYSTEM_ACCOUNT_DEFAULTS.generalOffsetCardId,
    fixedAssetSaleSortCode: resolved.fixedAssetSaleSortCode ?? CASE_SYSTEM_ACCOUNT_DEFAULTS.fixedAssetSaleSortCode,
    sortCodeDebit: resolved.sortCodeDebit ?? CASE_SYSTEM_ACCOUNT_DEFAULTS.sortCodeDebit,
    sortCodeCredit: resolved.sortCodeCredit ?? CASE_SYSTEM_ACCOUNT_DEFAULTS.sortCodeCredit,
    sortCodeDebitCredit: resolved.sortCodeDebitCredit ?? CASE_SYSTEM_ACCOUNT_DEFAULTS.sortCodeDebitCredit,
});

/**
 * Fetch effective per-case system account/sort-code settings once when caseId loads.
 * Falls back to CASE_SYSTEM_ACCOUNT_DEFAULTS on error or missing caseId.
 */
export default function useCaseSystemAccountSettings(caseId, token) {
    const [settings, setSettings] = useState(CASE_SYSTEM_ACCOUNT_DEFAULTS);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!caseId || !token) {
            setSettings(CASE_SYSTEM_ACCOUNT_DEFAULTS);
            return undefined;
        }

        let cancelled = false;
        setLoading(true);

        (async () => {
            try {
                const resp = await EnumsService.GetCaseSystemAccountSettings(caseId, token);
                if (!resp.ok || cancelled) return;
                const data = await resp.json();
                if (cancelled) return;
                setSettings(mergeResolved(data.resolved));
            } catch {
                if (!cancelled) setSettings(CASE_SYSTEM_ACCOUNT_DEFAULTS);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [caseId, token]);

    return { settings, loading };
}
